/**
 * The React surface of the chunk-aware audio player: the provider that owns one
 * engine, the context it publishes, and the hooks that read from it.
 *
 * The engine itself is `./engine` — plain TypeScript, no React — so everything
 * here is wiring: resolve the app seams, hand the engine a live view of them,
 * and keep the command atom mounted for the provider's lifetime.
 *
 * App-specific seams arrive as props:
 * - `userLoggedIn` — auth gating is app-specific (same convention as
 *   `query/user-hooks.ts`).
 * - `resolveAudioChunkSrc` / `readPersistedChunk` / `downloadedVoicesAtom` —
 *   mobile's offline downloads.
 * - `isPremiumUser` / `readIsPremiumUser` / `awaitEntitlementReady` — the
 *   app's one entitlement answer (mobile folds the RevenueCat SDK into it).
 *   Absent, the player reads the summary query alone, which is all web has.
 * - `docPackEnabled` — app-level doc-pack kill switch (web feeds the
 *   `disableDocPackLoading` flag; mobile has no flag infra yet so it defaults
 *   to true). Gated together with the pack version check.
 */
import { RegistryContext, useAtomValue } from '@effect/atom-react';
import { useContextAndErrorIfNull } from '@listening/utils/react-helpers';
import { useQueryClient } from '@tanstack/react-query';
import { Atom, type AtomRegistry } from 'effect/unstable/reactivity';
import {
  createContext,
  type PropsWithChildren,
  use,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { latestCell } from '../hooks/use-latest-cell';
import { useAudioDetailedView } from '../query/audio-detail';
import { useAppRuntime, useEq } from '../query/runtime-context';
import type {
  ChapterTtsChunk,
  ChapterTtsChunkParams,
} from '../query/tts-chunks';
import {
  useUpdateUserPreferencesMutation,
  useUserPreferencesQuery,
  useUserSummaryQuery,
} from '../query/user-hooks';
import { resolveCurrentVoice, useVoicesList } from '../query/voices';
import {
  isDocPackVersionUsable,
  type DocPackPageFetchMode,
} from '../doc-pack/source';
import {
  audioChunkCacheAtom,
  docPackChunkCacheKey,
  legacyChunkCacheKey,
  readAudioChunkCacheState,
} from './chunk-cache';
import {
  createPlaybackEngine,
  type AudioChunkSourceResolver,
  type PlaybackEngine,
  type PlaybackEngineSeams,
} from './engine';
import {
  createAudioChunkPlayerStoreHandle,
  type AudioChunkPlayerActions,
  type AudioChunkPlayerStoreHandle,
} from './playback-state';
import type { PlaybackCommandAtom } from './playback-command';
import { useAudioPoolPlayerContext } from './pool-player-context';

export * from './chunk-navigation';
export * from './engine';
export * from './playback-command';
export * from './playback-log';
export * from './playback-preferences';
export * from './playback-state';
export * from './playback-stats';
export { getDocPackPlaybackAnchorForPosition } from './timing-index';
export type { DocPackPlaybackAnchor } from './timing-index';
export { isAudioDetailReadyForStreaming } from '../query/audio-detail';

export const AudioChunkPlayerContext = createContext<{
  store: AudioChunkPlayerStoreHandle;
  storeActions: AudioChunkPlayerActions;
  /** The most recent command's outcome, for the rare consumer that needs
   * settlement rather than player state (`useAfterPlaybackCommand`). Commands
   * are latest-wins, so this reports the latest one — not the one a given call
   * site dispatched. */
  commandResult: PlaybackCommandAtom;
  userLoggedIn: boolean;
  docPackEnabled: boolean;
  docPackPageFetchMode: DocPackPageFetchMode;
  downloadedVoicesAtom: DownloadedVoicesAtom;
  isPremiumUser: boolean | undefined;
} | null>(null);

const renderHolds = new WeakMap<PlaybackEngine, ReadonlyArray<() => void>>();
const holdEngineLanes = (
  registry: AtomRegistry.AtomRegistry,
  engine: PlaybackEngine,
) => {
  const held = renderHolds.get(engine) ?? [
    registry.mount(engine.commandAtom),
    registry.mount(engine.automatedCommandAtom),
  ];
  renderHolds.set(engine, held);
  return held;
};

type DownloadedVoicesAtom = Atom.Atom<Readonly<Record<string, string>>>;
const noDownloadedVoicesAtom: DownloadedVoicesAtom = Atom.make({});

export function AudioChunkPlayerProviderInternal({
  children,
  initialAudioId,
  initialLookupAudioId,
  userLoggedIn,
  docPackEnabled = true,
  docPackPageFetchMode = 'auto',
  resolveAudioChunkSrc,
  readPersistedChunk,
  downloadedVoicesAtom = noDownloadedVoicesAtom,
  delayToPrefetchMs,
  isPremiumUser,
  readIsPremiumUser,
  awaitEntitlementReady,
}: PropsWithChildren & {
  userLoggedIn: boolean;
  docPackEnabled?: boolean;
  delayToPrefetchMs?: number;
  initialAudioId?: string;
  initialLookupAudioId?: string;
  docPackPageFetchMode?: DocPackPageFetchMode;
  resolveAudioChunkSrc?: AudioChunkSourceResolver;
  downloadedVoicesAtom?: DownloadedVoicesAtom;
  /** The app's entitlement answer for voice resolution, as a reactive value
   * the app re-renders this provider with. `undefined` falls back to the
   * summary query's `is_active_subscription`. */
  isPremiumUser?: boolean | undefined;
  /** Reads the same answer imperatively. Only the engine calls it, after
   * `awaitEntitlementReady`, so a load sees the answer it waited for even if
   * React has not re-rendered this provider yet. Never read during render: the
   * React Compiler would cache its first result. */
  readIsPremiumUser?: () => boolean;
  /** Resolves once the entitlement answer is real (or the app gave up
   * waiting). A load waits on it before deriving its first chunk key. */
  awaitEntitlementReady?: () => Promise<void>;
  /** Offline fallback (mobile downloads): chunk metadata persisted on device,
   * consulted when the in-memory chunk cache misses. Hits are promoted into
   * the cache so UI reads see them. `audioId` is the canonical document id
   * from the summary, which is how downloads key their records. */
  readPersistedChunk?: (
    params: ChapterTtsChunkParams & { audioId: string },
  ) => ChapterTtsChunk | undefined;
}) {
  const registry = use(RegistryContext);
  const queryClient = useQueryClient();
  const eq = useEq();
  const runtime = useAppRuntime();

  // One mutable view of the app seams, replaced every render and read — never
  // captured — by the engine.
  const seams: PlaybackEngineSeams = {
    player: useAudioPoolPlayerContext(),
    userLoggedIn,
    docPackEnabled,
    docPackPageFetchMode,
    resolveAudioChunkSrc,
    readPersistedChunk,
    downloadedVoicesAtom,
    delayToPrefetchMs,
    isPremiumUser,
    readIsPremiumUser,
    awaitEntitlementReady,
  };
  const [{ store, engine, latestSeams }] = useState(() => {
    const latestSeams = latestCell(seams);
    const store = createAudioChunkPlayerStoreHandle({
      registry,
      initialAudioId,
      initialLookupAudioId,
    });
    const engine = createPlaybackEngine({
      registry,
      runtime,
      queryClient,
      eq,
      store,
      read: latestSeams.get,
    });
    return { store, engine, latestSeams };
  });
  useLayoutEffect(() => {
    latestSeams.set(seams);
  });

  // An `Atom.fn` node is lazy: an unobserved one is disposed right after the
  // write forces it, which interrupts the very fiber that write started. Both
  // lanes are therefore held mounted for the provider's lifetime — and the
  // first hold is taken during RENDER, not in an effect: React flushes a
  // child's mount effect before the parent's, so a child that dispatches on
  // mount would otherwise force an unmounted atom.
  const renderMounts = useMemo(
    () => holdEngineLanes(registry, engine),
    [registry, engine],
  );
  useEffect(() => {
    const held = [
      registry.mount(engine.commandAtom),
      registry.mount(engine.automatedCommandAtom),
    ];
    for (const release of renderMounts) release();
    renderHolds.delete(engine);
    return () => {
      for (const release of held) release();
    };
  }, [registry, engine, renderMounts]);

  useUserPreferencesQuery({ enabled: userLoggedIn });
  useVoicesList();
  useAudioDetailedView(useAtomValue(store.atom, (s) => s.audioId));

  // `store` and `engine` are stable; only the primitives change. Memoized so
  // context consumers do not re-render on every provider render.
  const contextValue = useMemo(
    () => ({
      store,
      storeActions: engine.actions,
      commandResult: engine.commandAtom,
      userLoggedIn,
      docPackEnabled,
      docPackPageFetchMode,
      downloadedVoicesAtom,
      isPremiumUser,
    }),
    [
      store,
      engine,
      userLoggedIn,
      docPackEnabled,
      docPackPageFetchMode,
      downloadedVoicesAtom,
      isPremiumUser,
    ],
  );

  return (
    <AudioChunkPlayerContext value={contextValue}>
      {children}
    </AudioChunkPlayerContext>
  );
}

/** The loaded document as the player reads it: the decoded audio detail
 * itself, with no remapping layer in between. */
export function useDocumentSummary() {
  const { store } = useContextAndErrorIfNull(AudioChunkPlayerContext);
  const audioId = useAtomValue(store.atom, (state) => state.audioId);
  const audioDetailQuery = useAudioDetailedView(audioId);
  return { documentSummary: audioDetailQuery.data, audioDetailQuery };
}

export const usePreferedVoice = () => {
  const {
    userLoggedIn,
    downloadedVoicesAtom,
    isPremiumUser: appIsPremiumUser,
  } = useContextAndErrorIfNull(AudioChunkPlayerContext);
  const prefsMutation = useUpdateUserPreferencesMutation();
  const userPreferencesQuery = useUserPreferencesQuery({
    enabled: userLoggedIn,
  });
  const audioItem = useDocumentSummary().audioDetailQuery.data;
  const audioItemId = audioItem?.id;
  const audioPreferredVoiceName = useAtomValue(
    downloadedVoicesAtom,
    (voices) => (audioItemId === undefined ? undefined : voices[audioItemId]),
  );
  const userSummaryQuery = useUserSummaryQuery({ enabled: userLoggedIn });
  const isPremiumUser =
    appIsPremiumUser ??
    userSummaryQuery.data?.metering.is_active_subscription === true;
  const voiceOptionsQuery = useVoicesList();
  const voiceOptionsAll = voiceOptionsQuery.data ?? [];

  const preferredVoiceName =
    prefsMutation.isPending && prefsMutation.variables.voice
      ? prefsMutation.variables.voice
      : userPreferencesQuery.data?.voice;

  const resolve = (preferred: string | undefined) =>
    resolveCurrentVoice({
      audioItemLang: audioItem?.language,
      audioPreferredVoiceName,
      preferredVoiceName: preferred,
      isPremiumUser,
      voiceOptionsAll,
    });
  const currentVoice = resolve(preferredVoiceName);
  // What the engine fetches under (engine.ts getVoice reads the cache, not the
  // in-flight mutation), so chunk-cache keys must be derived from this one.
  const committedVoice = resolve(userPreferencesQuery.data?.voice);

  return {
    userPreferencesQuery,
    currentVoice,
    committedVoice,
    prefsMutation,
  };
};

/**
 * The current chunk as the UI sees it. Read-only: the engine's chunk fetcher
 * is the single fetch authority, and this hook just reads its cache under the
 * key derived from the store's selection — it never issues a request (the
 * previous TanStack version carried a live `queryFn` that could race a
 * duplicate fetch outside the fetcher's single-flight).
 *
 * Keeps the `{ data, isPending, isError }` shape its consumers read, with
 * TanStack's trichotomy: cached data is success (a fault never hides readable
 * chunk text — decision §5 in
 * docs/projects/active/pausing-audio-chunk-playing-refactor/playback-architecture-decisions.md);
 * no data splits into pending vs. error on the player's terminal fault state.
 */
export function useCurrentAudioChunkQuery() {
  const { store, docPackEnabled } = useContextAndErrorIfNull(
    AudioChunkPlayerContext,
  );
  const { committedVoice } = usePreferedVoice();
  const { documentSummary } = useDocumentSummary();

  const docPack = documentSummary?.doc_pack;
  // One subscription: the selection only matters as the cache key it produces,
  // so five separate field reads would re-render on changes that cannot move it.
  const cacheKey = useAtomValue(store.atom, (state) => {
    if (state.playbackMode === 'doc-pack') {
      return state.audioId &&
        state.docPackChunk &&
        docPack &&
        docPackEnabled &&
        isDocPackVersionUsable(docPack)
        ? docPackChunkCacheKey({
            audioId: state.audioId,
            docPackVersion: docPack.content_version,
            chunkId: state.docPackChunk.id,
            voice: committedVoice.name,
          })
        : undefined;
    }
    return state.audioId && state.chapterId && state.ttsId
      ? legacyChunkCacheKey({
          chapterId: state.chapterId,
          ttsId: state.ttsId,
          voice: committedVoice.name,
        })
      : undefined;
  });
  const playbackFault = useAtomValue(
    store.atom,
    (state) => state.playbackFault,
  );

  const data = useAtomValue(audioChunkCacheAtom, (state) =>
    cacheKey ? readAudioChunkCacheState(state, cacheKey) : undefined,
  );

  return {
    data,
    isPending: data === undefined && playbackFault === null,
    isError: data === undefined && playbackFault !== null,
  };
}
